import React, { useState, useEffect, Fragment } from 'react';
import { Translate } from '../../../contexts/Language';
import { Card, BreadCrumb, Modal } from '../../../components';
import { Service } from '../../../services';
import { Config } from '../../../config';
import { Row, Col, Button } from 'reactstrap';

function pathParamComponent() {
  const path = window.location.pathname
  const segments = path.split("/")
  const id = segments[4] // assuming /profile/123
  return id;
}

const JsonTable = ({ Data }) => {
    if (!Data || Data.length === 0) return null;

    return (
        <div className="table-responsive">
            {Data.map((obj, index) => (
                <table key={index} className="table table-sm table-bordered mb-3">
                    <tbody>
                        {Object.keys(obj || {}).map(key => (
                            <tr key={key}>
                                <td className="text-capitalize font-weight-bold" style={{ width: '30%' }}>{key}</td>
                                <td>{typeof obj[key] === 'object' ? JSON.stringify(obj[key], null, 2) : String(obj[key])}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ))}
        </div>
    );
};

export const DepositDetail = () => {
    const invoiceId = pathParamComponent();
    const [loading, setLoading] = useState(true);
    const [invoiceDetail, setInvoiceDetail] = useState(null);
    const [depositDetail, setDepositDetail] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchDepositDetails();
    }, [invoiceId]);

    const fetchDepositDetails = async () => {
        setLoading(true);
        Config.utilities.loadingFetch();

        try {
            // Fetch UniPayment invoice details
            const invoiceResponse = await Service.UniPayment.GetDepositDetails(invoiceId);
            
            if (invoiceResponse && invoiceResponse.error === false) {
                setInvoiceDetail(invoiceResponse.data);

                // Fetch internal deposit details using order_id
                if (invoiceResponse.data && invoiceResponse.data.order_id) {
                    const depositResponse = await Service.Deposit.Detail({ 
                        TransactionId: invoiceResponse.data.order_id 
                    });

                    if (depositResponse) {
                        // Parse JSON fields similar to Deposit/index.js
                        if (depositResponse.paymentResponse !== null) {
                            depositResponse.paymentResponse = JSON.parse(depositResponse.paymentResponse);

                            if (!depositResponse.paymentResponse.hasOwnProperty('paymentResponse') && 
                                !depositResponse.paymentResponse.hasOwnProperty('requestHeader')) {
                                let paymentResponse = depositResponse.paymentResponse;
                                depositResponse.paymentResponse = {};
                                depositResponse.paymentResponse.paymentResponse = paymentResponse;
                            }
                        }

                        if (depositResponse.common !== null) {
                            depositResponse.common = JSON.parse(depositResponse.common);
                        }

                        if (depositResponse.requestHeader !== null) {
                            depositResponse.requestHeader = JSON.parse(depositResponse.requestHeader);
                        }

                        if (depositResponse.comment !== null) {
                            let comment = JSON.parse(depositResponse.comment);
                            depositResponse.comment = comment.Comment;
                        }

                        setDepositDetail(depositResponse);
                    }
                }
            } else {
                setError(invoiceResponse.msg || 'Failed to fetch invoice details');
            }
        } catch (err) {
            setError('Error fetching deposit details: ' + err.message);
            console.error('Error fetching deposit details:', err);
        } finally {
            setLoading(false);
            Config.utilities.loadingFetch();
        }
    };

    const handleBack = () => {
        window.history.back();
    };

    if (loading) {
        return (
            <Fragment>
                <BreadCrumb title={Translate('UniPayment Deposit Detail')} />
                <Card title={Translate('Loading...')}>
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                </Card>
            </Fragment>
        );
    }

    if (error) {
        return (
            <Fragment>
                <BreadCrumb title={Translate('UniPayment Deposit Detail')} />
                <Card title={Translate('Error')}>
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                    <Button color="secondary" onClick={handleBack}>
                        {Translate('Back to List')}
                    </Button>
                </Card>
            </Fragment>
        );
    }

    return (
        <Fragment>
            <BreadCrumb title={Translate('UniPayment Deposit Detail')} />
            
            <Card title={Translate('UniPayment Deposit Information')}>
                <div className="mb-3">
                    <Button color="secondary" size="sm" onClick={handleBack}>
                        <i className="fas fa-arrow-left mr-1"></i> {Translate('Back to List')}
                    </Button>
                </div>

                {invoiceDetail && (
                    <Fragment>
                        {/* UniPayment Invoice Information */}
                        <Row className="mt-3">
                            <Col md={12}>
                                <p className="text-muted mb-2 font-12">
                                    <strong>{Translate('UniPayment Invoice Information')}</strong> [#{invoiceDetail.invoice_id}]
                                </p>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <dl className="row mb-0 ml-1 dl-striped">
                                    <dt className="col-sm-6">{Translate('Invoice ID')}</dt>
                                    <dd className="col-sm-6">{invoiceDetail.invoice_id}</dd>

                                    <dt className="col-sm-6">{Translate('Order ID')}</dt>
                                    <dd className="col-sm-6">{invoiceDetail.order_id}</dd>

                                    <dt className="col-sm-6">{Translate('Status')}</dt>
                                    <dd className="col-sm-6">
                                        <span className={`badge badge-${
                                            invoiceDetail.status === 'Complete' || invoiceDetail.status === 'Paid' ? 'success' :
                                            invoiceDetail.status === 'Confirmed' ? 'info' :
                                            invoiceDetail.status === 'New' ? 'primary' :
                                            invoiceDetail.status === 'Expired' ? 'warning' : 'danger'
                                        }`}>
                                            {invoiceDetail.status}
                                        </span>
                                    </dd>

                                    <dt className="col-sm-6">{Translate('Price Amount')}</dt>
                                    <dd className="col-sm-6">{invoiceDetail.price_amount} {invoiceDetail.price_currency}</dd>

                                    <dt className="col-sm-6">{Translate('Pay Amount')}</dt>
                                    <dd className="col-sm-6">{invoiceDetail.pay_amount} {invoiceDetail.pay_currency}</dd>

                                    <dt className="col-sm-6">{Translate('Paid Amount')}</dt>
                                    <dd className="col-sm-6">
                                        <strong>{invoiceDetail.paid_amount} {invoiceDetail.pay_currency}</strong>
                                    </dd>

                                    <dt className="col-sm-6">{Translate('Exchange Rate')}</dt>
                                    <dd className="col-sm-6">{invoiceDetail.exchange_rate}</dd>
                                </dl>
                            </Col>

                            <Col md={6}>
                                <dl className="row mb-0 ml-1 mr-1 dl-striped">
                                    <dt className="col-sm-6">{Translate('Create Time')}</dt>
                                    <dd className="col-sm-6">{invoiceDetail.create_time}</dd>

                                    <dt className="col-sm-6">{Translate('Expiration Time')}</dt>
                                    <dd className="col-sm-6">{invoiceDetail.expiration_time}</dd>

                                    <dt className="col-sm-6">{Translate('Confirm Time')}</dt>
                                    <dd className="col-sm-6">{invoiceDetail.confirm_time || '-'}</dd>

                                    <dt className="col-sm-6">{Translate('Payment Method')}</dt>
                                    <dd className="col-sm-6">{invoiceDetail.payment_method_type}</dd>

                                    <dt className="col-sm-6">{Translate('Network')}</dt>
                                    <dd className="col-sm-6">{invoiceDetail.network || '-'}</dd>

                                    <dt className="col-sm-6">{Translate('Transaction ID')}</dt>
                                    <dd className="col-sm-6" style={{ wordBreak: 'break-all' }}>
                                        {invoiceDetail.transaction_id || '-'}
                                    </dd>

                                    {invoiceDetail.note && (
                                        <>
                                            <dt className="col-sm-6">{Translate('Note')}</dt>
                                            <dd className="col-sm-6">{invoiceDetail.note}</dd>
                                        </>
                                    )}
                                </dl>
                            </Col>
                        </Row>

                        {/* Payment Address Information */}
                        {invoiceDetail.address && (
                            <Fragment>
                                <Row className="mt-4">
                                    <Col md={12}>
                                        <p className="text-muted mb-2 font-12">
                                            <strong>{Translate('Payment Address Information')}</strong>
                                        </p>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={12}>
                                        <dl className="row mb-0 ml-1 dl-striped">
                                            <dt className="col-sm-2">{Translate('Payment Address')}</dt>
                                            <dd className="col-sm-10" style={{ wordBreak: 'break-all' }}>
                                                {invoiceDetail.address}
                                            </dd>
                                        </dl>
                                    </Col>
                                </Row>
                            </Fragment>
                        )}

                        {/* Error Status */}
                        {invoiceDetail.error_status && (
                            <Fragment>
                                <Row className="mt-4">
                                    <Col md={12}>
                                        <div className="alert alert-warning" role="alert">
                                            <strong>{Translate('Error Status')}:</strong> {invoiceDetail.error_status}
                                        </div>
                                    </Col>
                                </Row>
                            </Fragment>
                        )}
                    </Fragment>
                )}

                {/* Internal Deposit Information */}
                {depositDetail && (
                    <Fragment>
                        <hr className="my-4" />
                        <Row>
                            <Col md={12}>
                                <p className="text-muted mb-2 font-12">
                                    <strong>{Translate('Internal Transfer Information')}</strong> [#{depositDetail.id}]
                                </p>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <dl className="row mb-0 ml-1 dl-striped">
                                    <dt className="col-sm-6">{Translate('Status')}</dt>
                                    <dd className="col-sm-6">
                                        <span className={"badge pb-1 font-12 payment-status " + depositDetail.statusClass}>
                                            {Translate(depositDetail.statusTitle)}
                                        </span>
                                    </dd>

                                    <dt className="col-sm-6">{Translate('Login')}</dt>
                                    <dd className="col-sm-6">{depositDetail.login}</dd>

                                    <dt className="col-sm-6">{Translate('PSP')}</dt>
                                    <dd className="col-sm-6">
                                        {depositDetail.transferLogo && (
                                            <img 
                                                src={"/assets/psp-logo/" + depositDetail.transferLogo} 
                                                alt="" 
                                                width="30" 
                                                className="mb-1" 
                                            />
                                        )}
                                        &nbsp; {depositDetail.transferOption}
                                    </dd>

                                    <dt className="col-sm-6">{Translate('Amount')}</dt>
                                    <dd className="col-sm-6">{depositDetail.amount} {depositDetail.transferCurrency}</dd>

                                    <dt className="col-sm-6">{Translate('Calculated Amount')}</dt>
                                    <dd className="col-sm-6">
                                        {(depositDetail.calculatedAmount ? depositDetail.calculatedAmount.toFixed(2) : 0)} {depositDetail.accountCurrency || ""}
                                    </dd>

                                    <dt className="col-sm-6">{Translate('Exchange Rate')}</dt>
                                    <dd className="col-sm-6">{depositDetail.exchangeRate}</dd>
                                </dl>
                            </Col>

                            <Col md={6}>
                                <dl className="row mb-0 ml-1 mr-1 dl-striped">
                                    <dt className="col-sm-6">{Translate('Date')}</dt>
                                    <dd className="col-sm-6">{depositDetail.insertDate}</dd>

                                    <dt className="col-sm-6">{Translate('Fullname')}</dt>
                                    <dd className="col-sm-6">{depositDetail.fullname}</dd>

                                    <dt className="col-sm-6">{Translate('Meta Status')}</dt>
                                    <dd className="col-sm-6">{depositDetail.metaStatus}</dd>

                                    <dt className="col-sm-6">{Translate('Meta Ticket')}</dt>
                                    <dd className="col-sm-6">{depositDetail.metaTicket}</dd>

                                    {depositDetail.comment && (
                                        <>
                                            <dt className="col-sm-6">{Translate('Comment')}</dt>
                                            <dd className="col-sm-6">{depositDetail.comment}</dd>
                                        </>
                                    )}
                                </dl>
                            </Col>
                        </Row>

                        {/* Common Information */}
                        {depositDetail.common && (
                            <Fragment>
                                <Row className="mt-4">
                                    <Col md={12}>
                                        <p className="text-muted mb-2 font-12">
                                            <strong>{Translate('Common Information')}</strong>
                                        </p>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <dl className="row mb-0 ml-1 dl-striped">
                                            {Object.keys(depositDetail.common).map(key => (
                                                <Fragment key={key}>
                                                    <dt className="col-sm-6 text-capitalize">{key}</dt>
                                                    <dd className="col-sm-6">{depositDetail.common[key]}</dd>
                                                </Fragment>
                                            ))}
                                        </dl>
                                    </Col>
                                </Row>
                            </Fragment>
                        )}

                        {/* Payment Response */}
                        {depositDetail.paymentResponse && (
                            <Fragment>
                                <Row className="mt-4">
                                    <Col md={12}>
                                        <p className="text-muted mb-2 font-12">
                                            <strong>{Translate('Payment Response')}</strong> 
                                            {depositDetail.paymentResponseDate && (
                                                <small className="ml-2">[{depositDetail.paymentResponseDate}]</small>
                                            )}
                                        </p>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={12}>
                                        <JsonTable 
                                            Data={[
                                                depositDetail.paymentResponse.paymentResponse, 
                                                depositDetail.paymentResponse.requestHeader
                                            ]} 
                                        />
                                    </Col>
                                </Row>
                            </Fragment>
                        )}

                        {/* Request Header */}
                        {depositDetail.requestHeader && (
                            <Fragment>
                                <Row className="mt-4">
                                    <Col md={12}>
                                        <p className="text-muted mb-2 font-12">
                                            <strong>{Translate('Request Header')}</strong>
                                        </p>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={12}>
                                        <JsonTable Data={[depositDetail.requestHeader]} />
                                    </Col>
                                </Row>
                            </Fragment>
                        )}
                    </Fragment>
                )}

                {!depositDetail && invoiceDetail && invoiceDetail.order_id && (
                    <div className="alert alert-info mt-4" role="alert">
                        {Translate('Internal deposit details not found for this transaction.')}
                    </div>
                )}

                <div className="mt-4">
                    <Button color="secondary" onClick={handleBack}>
                        {Translate('Back to List')}
                    </Button>
                </div>
            </Card>
        </Fragment>
    );
};

export default DepositDetail;